import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RoutingMap, { MapLine, MapStop } from '../../components/RoutingMap';
import type { WorldRide } from '../../types/routing';
import { useTenant } from '../../context/TenantContext';
import { todayInNewYork } from '../../utils/format';
import * as api from '../../services/routing/api';
import {
  useTenantDriversQuery,
  useTenantCompaniesQuery,
  useTenantFleetsQuery,
  useZipCodesQuery,
  useWorldRidesQuery,
  useWorldRoutesQuery
} from '../../services/routing/queries';

type WorldKind = 'drivers' | 'rides' | 'routes' | 'fleets' | 'companies' | 'zip-codes';

const TEMPLATES: Record<WorldKind, unknown[]> = {
  drivers: [
    {
      id: 55,
      name: 'Dana',
      vehicle: {
        car_id: 9,
        seats: 4,
        wav_seats: 2,
        wav_car: true,
        car_type: 'ambulette',
        plate: 'ABC-123',
        make_model: 'Ford Transit'
      }
    }
  ],
  rides: [
    {
      id: 9001,
      pickup_at: '2026-08-20 14:00:00',
      dropoff_at: '2026-08-20 14:30:00',
      pickup_lat: 40.71,
      pickup_lon: -74.0,
      dropoff_lat: 40.75,
      dropoff_lon: -73.98,
      company_id: 17
    }
  ],
  routes: [
    {
      id: 601,
      driver_id: 55,
      fleet_id: 12,
      from_datetime: '2026-08-20 08:00:00',
      to_datetime: '2026-08-20 20:00:00',
      starting_zip_code: '11201'
    }
  ],
  fleets: [
    { id: 12, name: 'Acme Cars' },
    { id: 77, name: 'Metro Rides' }
  ],
  companies: [
    { id: 17, name: 'Acme Cars Inc' },
    { id: 42, name: 'Metro Medical' }
  ],
  'zip-codes': [
    { code: '11201', lat: 40.694, lon: -73.99 },
    { code: '10001', lat: 40.75, lon: -73.997 }
  ]
};

const LABELS: Record<WorldKind, string> = {
  drivers: 'Drivers & vehicles',
  rides: 'Rides',
  routes: 'Routes',
  fleets: 'Fleets',
  companies: 'Companies',
  'zip-codes': 'Zip codes'
};

const BLURBS: Record<WorldKind, string> = {
  drivers:
    'What each driver_id drives, said once instead of on every route. A route posted without ' +
    'seats, wav_car, car_type or car_id takes them from here; a route that states its own wins. ' +
    'car_type is one of ambulance, ambulette, fhv, green_taxi, minivan, sedan, suv, van — ' +
    'wheelchair access is wav_car and wav_seats, not a car type.',
  rides:
    'The demand a run plans against. Ids are yours to choose; company_id is what lets a run be ' +
    'scoped to particular companies on the run screen.',
  routes:
    'The drivers on shift. A route joins a run when its from_datetime falls inside the planned ' +
    'New York day. It starts from its starting_zip_code, or from start_lat/start_lon if you ' +
    'have coordinates instead — the registered vehicle, and the zip ' +
    'lookup, are folded in as it is saved.',
  fleets:
    'Names for the fleet ids your routes carry — what turns “Fleet 12” into “Acme Cars” on the ' +
    'run screen. Naming a fleet does not put it in that picker; a route carrying its id does.',
  companies:
    'Names for the company ids your rides carry — what turns “Company 17” into “Acme Cars Inc” ' +
    'in the run screen’s company picker. Naming a company does not put it in that picker; a ' +
    'ride carrying its id does.',
  'zip-codes':
    'Where each zip code is — reference data shared by every tenant. All 41,917 US codes ship ' +
    'in a migration, so this is normally full already. A route carrying starting_zip_code ' +
    'instead of coordinates starts from the point here, resolved as the route is saved; save ' +
    'one only to add or correct what the seed got wrong.'
};

const template = (kind: WorldKind) => JSON.stringify(TEMPLATES[kind], null, 2);

// A whole fleet's drivers is megabytes of JSON. Posting it as one body is what the server would
// rather have, but a browser will not hold it in a controlled <textarea> — so a file is read
// straight to the parser and sent in batches this size, which also gives something to show
// progress against. Entries upsert by id, so a batch that fails can simply be re-sent.
const BATCH = 500;

// Past this the editor stops being an editor: React re-renders the whole string on each
// keystroke. Big payloads belong in a file.
const EDITOR_MAX_CHARS = 200_000;

// Every table is one server-side page. Thousands of rows is thousands of DOM nodes and a
// megabyte of JSON to get them; the server cuts the page and says what it was cut from.
const PAGE_SIZES = [50, 100, 250, 500];

const NO_OFFSETS: Record<WorldKind, number> = {
  drivers: 0,
  companies: 0,
  rides: 0,
  routes: 0,
  fleets: 0,
  'zip-codes': 0
};

// FileReader rather than Blob.text(): same result, and supported everywhere this runs.
const readText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(`could not read ${file.name}`));
    reader.readAsText(file);
  });

const parseJson = (text: string, what: string): unknown => {
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON in ${what}: ${(error as Error).message}`);
  }
};

const asEntries = (parsed: unknown, kind: WorldKind): unknown[] => {
  const entries = Array.isArray(parsed)
    ? parsed
    : ((parsed as Record<string, unknown> | null)?.[kind] as unknown[] | undefined);
  if (!Array.isArray(entries)) {
    throw new Error(`Provide a JSON array of ${kind}, or an object with a "${kind}" array`);
  }
  return entries;
};

/** The listing resolves the company name for display; posting it back would be noise. */
const postableRide = ({ company_name: _named, ...ride }: WorldRide) => ride;

const singular = (kind: WorldKind) =>
  kind === 'drivers' ? 'driver' : kind === 'zip-codes' ? 'zip code' : kind.slice(0, -1);

const Empty = ({ span, what }: { span: number; what: string }) => (
  <TableRow>
    <TableCell colSpan={span}>
      <Typography color="text.secondary">Nothing saved yet — {what}</Typography>
    </TableCell>
  </TableRow>
);

const RowActions = ({
  kind,
  id,
  entity,
  onEdit,
  onDelete
}: {
  kind: WorldKind;
  id: number;
  entity: unknown;
  onEdit: (kind: WorldKind, entity: unknown) => void;
  onDelete: (kind: WorldKind, id: number) => void;
}) => (
  <Stack direction="row" justifyContent="flex-end">
    <IconButton
      size="small"
      aria-label={`edit ${singular(kind)} ${id}`}
      onClick={() => onEdit(kind, entity)}
    >
      <EditIcon fontSize="small" />
    </IconButton>
    <IconButton
      size="small"
      aria-label={`delete ${singular(kind)} ${id}`}
      onClick={() => onDelete(kind, id)}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Stack>
);

const Pager = ({
  kind,
  total,
  offset,
  rowsPerPage,
  onOffset,
  onRowsPerPage
}: {
  kind: WorldKind;
  total: number;
  offset: number;
  rowsPerPage: number;
  onOffset: (offset: number) => void;
  onRowsPerPage: (rows: number) => void;
}) => (
  <TablePagination
    component="div"
    count={total}
    page={Math.floor(offset / rowsPerPage)}
    rowsPerPage={rowsPerPage}
    rowsPerPageOptions={PAGE_SIZES}
    labelRowsPerPage={`${LABELS[kind]} per page`}
    onPageChange={(_event, page) => onOffset(page * rowsPerPage)}
    onRowsPerPageChange={event => onRowsPerPage(Number(event.target.value))}
    getItemAriaLabel={type => `${type} page of ${kind}`}
  />
);

export const WorldPage = () => {
  const { tenant } = useTenant();
  const [tab, setTab] = useState<WorldKind>('drivers');
  // Today's plan is what anyone opening this is nearly always after; "all days" is one click.
  const [dayFilter, setDayFilter] = useState(todayInNewYork);
  const [editors, setEditors] = useState<Record<WorldKind, string>>({
    drivers: template('drivers'),
    companies: template('companies'),
    rides: template('rides'),
    routes: template('routes'),
    fleets: template('fleets'),
    'zip-codes': template('zip-codes')
  });
  const [rowsPerPage, setRowsPerPage] = useState(api.PAGE_SIZE);
  const [offsets, setOffsets] = useState<Record<WorldKind, number>>(NO_OFFSETS);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // The day and the page size change what every page holds, and so does picking another tenant —
  // page 4 of the old answer is not page 4 of the new one.
  useEffect(() => setOffsets(NO_OFFSETS), [tenant, dayFilter, rowsPerPage]);

  const page = (kind: WorldKind) => ({ offset: offsets[kind], limit: rowsPerPage });
  // The day filter is what the run screen means by a day, so the server applies it, not the table.
  const worldPage = (kind: 'rides' | 'routes') => ({ ...page(kind), day: dayFilter || undefined });

  const {
    data: rideData,
    isLoading: ridesLoading,
    error
  } = useWorldRidesQuery(tenant, worldPage('rides'));
  const { data: routeData, isLoading: routesLoading } = useWorldRoutesQuery(
    tenant,
    worldPage('routes')
  );
  const { data: fleetData } = useTenantFleetsQuery(tenant, page('fleets'));
  const { data: driverData } = useTenantDriversQuery(tenant, page('drivers'));
  const { data: zipData } = useZipCodesQuery(tenant, page('zip-codes'));
  const { data: companyData } = useTenantCompaniesQuery(tenant, page('companies'));
  const isLoading = ridesLoading || routesLoading;
  const fleets = fleetData?.fleets ?? [];
  const drivers = driverData?.drivers ?? [];
  const zipCodes = zipData?.zip_codes ?? [];
  const companies = companyData?.companies ?? [];
  const rides = rideData?.rides ?? [];
  const routes = routeData?.routes ?? [];
  const filterable = tab === 'rides' || tab === 'routes';
  const totalForTab =
    (tab === 'rides' ? rideData?.total_all_days : routeData?.total_all_days) ?? 0;

  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const busy = progress !== null;

  const setEditor = (kind: WorldKind, value: string) =>
    setEditors(current => ({ ...current, [kind]: value }));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['world-rides'] });
    queryClient.invalidateQueries({ queryKey: ['world-routes'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-fleets'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-drivers'] });
    queryClient.invalidateQueries({ queryKey: ['zip-codes'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-companies'] });
    // The run screen offers companies from the rides, named by this list.
    queryClient.invalidateQueries({ queryKey: ['companies-in-rides'] });
    // The run screen offers fleets from the routes, named by this list.
    queryClient.invalidateQueries({ queryKey: ['fleets-in-routes'] });
  };
  const onError = (mutationError: unknown) =>
    enqueueSnackbar((mutationError as Error).message, { variant: 'error' });

  const postFor = (kind: WorldKind, entries: unknown[]) => {
    if (kind === 'rides') return api.postWorldRides(tenant, entries);
    if (kind === 'routes') return api.postWorldRoutes(tenant, entries);
    if (kind === 'drivers') return api.postTenantDrivers(tenant, entries);
    if (kind === 'zip-codes') return api.postZipCodes(entries);
    if (kind === 'companies') return api.postTenantCompanies(tenant, entries);
    return api.postTenantFleets(tenant, entries as { id: number; name: string }[]);
  };

  const clearFor = (kind: WorldKind) => {
    if (kind === 'rides') return api.clearWorldRides(tenant);
    if (kind === 'routes') return api.clearWorldRoutes(tenant);
    if (kind === 'drivers') return api.clearTenantDrivers(tenant);
    if (kind === 'companies') return api.clearTenantCompanies(tenant);
    return api.clearTenantFleets(tenant);
  };

  /** Sends in batches so one oversized request never has to succeed. */
  const save = async (kind: WorldKind, entries: unknown[]) => {
    if (!entries.length) {
      enqueueSnackbar(`No ${kind} to save`, { variant: 'error' });
      return;
    }
    setProgress({ done: 0, total: entries.length });
    let saved = 0;
    try {
      for (let start = 0; start < entries.length; start += BATCH) {
        const batch = entries.slice(start, start + BATCH);
        const result = await postFor(kind, batch);
        saved += result.accepted;
        setProgress({ done: Math.min(start + BATCH, entries.length), total: entries.length });
      }
      enqueueSnackbar(`Saved ${saved} ${kind} for tenant ${tenant}`);
    } catch (postError) {
      // Batches already sent are stored; re-sending the file overwrites them by id.
      enqueueSnackbar(
        `${(postError as Error).message} — ${saved} of ${entries.length} ${kind} saved before it `
          + 'failed; fix the file and save it again',
        { variant: 'error' }
      );
    } finally {
      setProgress(null);
      refresh();
    }
  };

  const postJson = async (kind: WorldKind) => {
    try {
      await save(kind, asEntries(parseJson(editors[kind], 'the editor'), kind));
    } catch (parseError) {
      enqueueSnackbar((parseError as Error).message, { variant: 'error' });
    }
  };

  // The file is parsed and posted without ever reaching component state — an 11MB payload in a
  // controlled <textarea> is what truncates.
  const postFile = async (kind: WorldKind, file: File) => {
    let entries: unknown[];
    try {
      entries = asEntries(parseJson(await readText(file), file.name), kind);
    } catch (parseError) {
      enqueueSnackbar((parseError as Error).message, { variant: 'error' });
      return;
    }
    if (JSON.stringify(entries[0] ?? {}).length * entries.length < EDITOR_MAX_CHARS) {
      setEditor(kind, JSON.stringify(entries, null, 2));
    } else {
      setEditor(kind, `// ${entries.length} ${kind} loaded from ${file.name} — too large to show`);
    }
    await save(kind, entries);
  };

  const clearAll = async (kind: WorldKind) => {
    if (!window.confirm(`Delete ALL ${kind} saved for tenant "${tenant}"?`)) return;
    try {
      const result = await clearFor(kind);
      enqueueSnackbar(`Deleted ${result.deleted} ${kind}`);
      refresh();
    } catch (clearError) {
      onError(clearError);
    }
  };

  const deleteZip = async (code: string) => {
    try {
      await api.deleteZipCode(code);
      refresh();
    } catch (deleteError) {
      onError(deleteError);
    }
  };

  const deleteOne = async (kind: WorldKind, id: number) => {
    try {
      if (kind === 'rides') await api.deleteWorldRide(tenant, id);
      else if (kind === 'routes') await api.deleteWorldRoute(tenant, id);
      else if (kind === 'drivers') await api.deleteTenantDriver(tenant, id);
      else if (kind === 'companies') await api.deleteTenantCompany(tenant, id);
      else await api.deleteTenantFleet(tenant, id);
      refresh();
    } catch (deleteError) {
      onError(deleteError);
    }
  };

  /** Re-posting an entity is how you edit it, so load the row into the editor as it stands. */
  const edit = (kind: WorldKind, entity: unknown) => {
    setEditor(kind, JSON.stringify([entity], null, 2));
    enqueueSnackbar('Loaded into the editor — change it and save to update by id');
  };

  const dryRunWorld = async () => {
    const day = window.prompt('Planned day (America/New_York), e.g. 2026-08-20:');
    if (!day) return;
    try {
      const result = await api.createRun({
        from_datetime: day,
        tenant
      });
      enqueueSnackbar(`Run #${result.run_id} started`);
      navigate(`/runs/${result.run_id}`);
    } catch (runError) {
      onError(runError);
    }
  };

  const stops: MapStop[] = [];
  const lines: MapLine[] = [];
  rides.forEach(ride => {
    stops.push({
      kind: 'pickup',
      lat: ride.pickup_lat,
      lon: ride.pickup_lon,
      label: `ride ${ride.id} pickup · ${ride.pickup_at}`
    });
    stops.push({
      kind: 'dropoff',
      lat: ride.dropoff_lat,
      lon: ride.dropoff_lon,
      label: `ride ${ride.id} dropoff · ${ride.dropoff_at}`
    });
    lines.push({
      kind: 'ride',
      points: [
        [ride.pickup_lat, ride.pickup_lon],
        [ride.dropoff_lat, ride.dropoff_lon]
      ]
    });
  });
  routes.forEach(route => {
    if (route.start_lat != null && route.start_lon != null) {
      stops.push({
        kind: 'route-start',
        lat: route.start_lat,
        lon: route.start_lon,
        label: `route ${route.id} start`
      });
    }
  });

  // Every count is the server's total for that listing, not how many rows this page happens to
  // hold — the chips and tab labels have to mean "saved", not "shown".
  const counts: Record<WorldKind, number> = {
    drivers: driverData?.total ?? 0,
    rides: rideData?.total ?? 0,
    routes: routeData?.total ?? 0,
    fleets: fleetData?.total ?? 0,
    companies: companyData?.total ?? 0,
    'zip-codes': zipData?.total ?? 0
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={dryRunWorld}>
            Run this world
          </Button>
          {(['drivers', 'rides', 'routes', 'fleets', 'companies', 'zip-codes'] as WorldKind[]).map(kind => (
            <Chip key={kind} label={`${LABELS[kind]}: ${counts[kind]}`} size="small" />
          ))}
          <Typography variant="body2" color="text.secondary">
            Everything here is saved against tenant “{tenant}” and survives a restart. Saving an
            id that already exists updates it.
          </Typography>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {isLoading && <CircularProgress aria-label="loading world" />}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          WORLD MAP
        </Typography>
        {stops.length ? (
          <>
            <RoutingMap stops={stops} lines={lines} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              The rides and routes on the pages currently open — turn a page to map the next ones.
            </Typography>
          </>
        ) : (
          <Typography color="text.secondary">No rides or routes saved for this tenant.</Typography>
        )}
      </Paper>

      <Paper>
        <Tabs
          value={tab}
          onChange={(_event, next) => setTab(next as WorldKind)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {(['drivers', 'rides', 'routes', 'fleets', 'companies', 'zip-codes'] as WorldKind[]).map(kind => (
            <Tab key={kind} value={kind} label={`${LABELS[kind]} (${counts[kind]})`} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {BLURBS[tab]}
          </Typography>
          {filterable && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <TextField
                size="small"
                type="date"
                label="Planned day (NY)"
                value={dayFilter}
                onChange={event => setDayFilter(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'aria-label': 'planned day' }}
              />
              {dayFilter && (
                <Link component="button" variant="caption" underline="hover" onClick={() => setDayFilter('')}>
                  all days
                </Link>
              )}
              <Typography variant="caption" color="text.secondary">
                {dayFilter
                  ? `${counts[tab]} of ${totalForTab} ${LABELS[tab].toLowerCase()} planned on ${dayFilter}`
                  : `all ${totalForTab} ${LABELS[tab].toLowerCase()}, every day`}
              </Typography>
            </Stack>
          )}

          {tab === 'drivers' && (
            <>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>Driver</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell align="right">Seats</TableCell>
                    <TableCell>Routes</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drivers.length === 0 && <Empty span={6} what="register a driver below." />}
                  {drivers.map(driver => (
                    <TableRow key={driver.id}>
                      <TableCell>{driver.id}</TableCell>
                      <TableCell>
                        {driver.name ?? (driver.registered ? '—' : 'unregistered')}
                      </TableCell>
                      <TableCell>
                        {[driver.vehicle.car_type, driver.vehicle.make_model, driver.vehicle.plate]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </TableCell>
                      <TableCell align="right">{driver.vehicle.seats ?? '—'}</TableCell>
                      <TableCell>
                        {driver.route_ids.length ? driver.route_ids.join(', ') : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {driver.registered ? (
                          <RowActions
                            kind="drivers"
                            id={driver.id}
                            entity={{
                              id: driver.id,
                              name: driver.name,
                              vehicle: driver.vehicle
                            }}
                            onEdit={edit}
                            onDelete={deleteOne}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            named by a route
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager
              kind="drivers"
              total={counts['drivers']}
              offset={offsets['drivers']}
              rowsPerPage={rowsPerPage}
              onOffset={offset => setOffsets(current => ({ ...current, 'drivers': offset }))}
              onRowsPerPage={setRowsPerPage}
            />
            </>
          )}

          {tab === 'rides' && (
            <>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Id</TableCell>
                    <TableCell>Pickup (UTC)</TableCell>
                    <TableCell>Dropoff</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Zones</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rides.length === 0 && <Empty span={6} what="post some demand below." />}
                  {rides.map(ride => (
                    <TableRow key={ride.id}>
                      <TableCell>{ride.id}</TableCell>
                      <TableCell>{ride.pickup_at}</TableCell>
                      <TableCell>{ride.dropoff_at}</TableCell>
                      <TableCell>{ride.company_name ?? ride.company_id ?? '—'}</TableCell>
                      <TableCell>
                        {ride.pickup_zone_id ?? '—'} → {ride.dropoff_zone_id ?? '—'}
                      </TableCell>
                      <TableCell align="right">
                        <RowActions
                          kind="rides"
                          id={ride.id}
                          entity={postableRide(ride)}
                          onEdit={edit}
                          onDelete={deleteOne}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager
              kind="rides"
              total={counts['rides']}
              offset={offsets['rides']}
              rowsPerPage={rowsPerPage}
              onOffset={offset => setOffsets(current => ({ ...current, 'rides': offset }))}
              onRowsPerPage={setRowsPerPage}
            />
            </>
          )}

          {tab === 'routes' && (
            <>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Id</TableCell>
                    <TableCell>Shift (UTC)</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Fleet</TableCell>
                    <TableCell>Starts</TableCell>
                    <TableCell align="right">Seats</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {routes.length === 0 && <Empty span={7} what="put a driver on shift below." />}
                  {routes.map(route => {
                    // vehicle_type is derived and the fleet name is resolved for the listing —
                    // neither is something you post back.
                    const { vehicle_type: _derived, fleet_name: _named, ...postable } = route;
                    return (
                      <TableRow key={route.id}>
                        <TableCell>{route.id}</TableCell>
                        <TableCell>
                          {route.from_datetime} → {route.to_datetime}
                        </TableCell>
                        <TableCell>{route.driver_id ?? '—'}</TableCell>
                        <TableCell>{route.fleet_name}</TableCell>
                        <TableCell>
                          {route.starting_zip_code
                            ? route.starting_zip_code
                            : route.start_lat == null
                              ? '—'
                              : `${route.start_lat}, ${route.start_lon}`}
                        </TableCell>
                        <TableCell align="right">{route.seats ?? '—'}</TableCell>
                        <TableCell align="right">
                          <RowActions
                            kind="routes"
                            id={route.id}
                            entity={postable}
                            onEdit={edit}
                            onDelete={deleteOne}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager
              kind="routes"
              total={counts['routes']}
              offset={offsets['routes']}
              rowsPerPage={rowsPerPage}
              onOffset={offset => setOffsets(current => ({ ...current, 'routes': offset }))}
              onRowsPerPage={setRowsPerPage}
            />
            </>
          )}

          {tab === 'companies' && (
            <>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Id</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Rides</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.length === 0 && <Empty span={4} what="name a company below." />}
                  {companies.map(company => (
                    <TableRow key={company.id}>
                      <TableCell>{company.id}</TableCell>
                      <TableCell>{company.name}</TableCell>
                      <TableCell align="right">{company.rides ?? 0}</TableCell>
                      <TableCell align="right">
                        <RowActions
                          kind="companies"
                          id={company.id}
                          entity={{ id: company.id, name: company.name }}
                          onEdit={edit}
                          onDelete={deleteOne}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager
              kind="companies"
              total={counts['companies']}
              offset={offsets['companies']}
              rowsPerPage={rowsPerPage}
              onOffset={offset => setOffsets(current => ({ ...current, 'companies': offset }))}
              onRowsPerPage={setRowsPerPage}
            />
            </>
          )}

          {tab === 'zip-codes' && (
            <>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 140 }}>Code</TableCell>
                    <TableCell>Latitude</TableCell>
                    <TableCell>Longitude</TableCell>
                    <TableCell align="right">Routes starting here</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {zipCodes.length === 0 && (
                    <Empty span={5} what="apply the migrations (task run-store:init) to seed them." />
                  )}
                  {zipCodes.map(zip => (
                    <TableRow key={zip.code}>
                      <TableCell>{zip.code}</TableCell>
                      <TableCell>{zip.lat}</TableCell>
                      <TableCell>{zip.lon}</TableCell>
                      <TableCell align="right">{zip.routes}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end">
                          <IconButton
                            size="small"
                            aria-label={`edit zip code ${zip.code}`}
                            onClick={() => edit('zip-codes', { code: zip.code, lat: zip.lat, lon: zip.lon })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`delete zip code ${zip.code}`}
                            onClick={() => deleteZip(zip.code)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager
              kind="zip-codes"
              total={counts['zip-codes']}
              offset={offsets['zip-codes']}
              rowsPerPage={rowsPerPage}
              onOffset={offset => setOffsets(current => ({ ...current, 'zip-codes': offset }))}
              onRowsPerPage={setRowsPerPage}
            />
            </>
          )}

          {tab === 'fleets' && (
            <>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Id</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Routes</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fleets.length === 0 && <Empty span={4} what="name a fleet below." />}
                  {fleets.map(fleet => (
                    <TableRow key={fleet.id}>
                      <TableCell>{fleet.id}</TableCell>
                      <TableCell>{fleet.name}</TableCell>
                      <TableCell align="right">{fleet.routes ?? 0}</TableCell>
                      <TableCell align="right">
                        <RowActions
                          kind="fleets"
                          id={fleet.id}
                          entity={{ id: fleet.id, name: fleet.name }}
                          onEdit={edit}
                          onDelete={deleteOne}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pager
              kind="fleets"
              total={counts['fleets']}
              offset={offsets['fleets']}
              rowsPerPage={rowsPerPage}
              onOffset={offset => setOffsets(current => ({ ...current, 'fleets': offset }))}
              onRowsPerPage={setRowsPerPage}
            />
            </>
          )}

          <TextField
            label={`POST /api/v1/${tab} — JSON array`}
            multiline
            minRows={8}
            fullWidth
            value={editors[tab]}
            onChange={event => setEditor(tab, event.target.value)}
            disabled={busy}
            sx={{ mt: 2, '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="contained" onClick={() => postJson(tab)} disabled={busy}>
              Save {LABELS[tab].toLowerCase()}
            </Button>
            <Button component="label" disabled={busy}>
              Save from file…
              <input
                type="file"
                accept="application/json,.json"
                hidden
                aria-label={`${tab} JSON file`}
                onChange={event => {
                  const file = event.target.files?.[0];
                  // Clear the input so picking the same file twice fires again.
                  event.target.value = '';
                  if (file) postFile(tab, file);
                }}
              />
            </Button>
            <Button onClick={() => setEditor(tab, template(tab))} disabled={busy}>
              Reset to example
            </Button>
            {progress && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 220 }}>
                <LinearProgress
                  variant="determinate"
                  value={(progress.done / progress.total) * 100}
                  sx={{ flexGrow: 1 }}
                  aria-label={`saving ${tab}`}
                />
                <Typography variant="caption" color="text.secondary">
                  {progress.done}/{progress.total}
                </Typography>
              </Stack>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {tab !== 'zip-codes' && (
              <Button color="error" onClick={() => clearAll(tab)} disabled={busy}>
                Delete all
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            A big export belongs in a file — the browser truncates a paste of this size, and the
            file is read straight to the API in batches of {BATCH}. Entries upsert by id, so
            saving the same file twice is safe.
          </Typography>
        </Box>
      </Paper>
    </Stack>
  );
};

export default WorldPage;
